import { Box, Button, Typography } from "@mui/material";
import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import ReadOnlyTooltip from "components/common/ReadOnlyTooltip";
import PaginationControls from "components/fileStorage/PaginationControls";
import ListToolbar from "components/secureExchange/ListToolbar";
import SecureExchangeBreadcrumbs from "components/secureExchange/SecureExchangeBreadcrumbs";
import SecureExchangeHeader from "components/secureExchange/SecureExchangeHeader";
import SecureExchangeShell from "components/secureExchange/SecureExchangeShell";
import SecureFileTable from "components/secureExchange/SecureFileTable";
import SecureUploadDialog from "components/secureExchange/SecureUploadDialog";
import {
  type FileSort,
  nextFileSort,
} from "components/secureExchange/fileModel";
import { CollectionIcon } from "components/secureExchange/icons";
import { useDebouncedValue } from "components/secureExchange/useDebouncedValue";
import { useFileActions } from "components/secureExchange/useFileActions";
import { useSecureExchangePaths } from "components/secureExchange/useSecureExchangePaths";
import {
  useGetSecureCollectionQuery,
  useGetSecureFilesQuery,
} from "features/secureUploads/secureUploads";
import { usePermissions } from "hooks/usePermissions";
import { useViewAs } from "hooks/useViewAs";
import { ACTIONS } from "utils/access";

const PAGE_SIZE = 25;

/** A collection's page: the files filed in it. */
export default function SecureExchangeCollectionPage() {
  const params = useParams<{
    clientId: string;
    projectId: string;
    collectionId: string;
  }>();
  const clientId = Number(params.clientId);
  const projectId = Number(params.projectId);
  const collectionId = Number(params.collectionId);
  const paths = useSecureExchangePaths();
  const { readOnly } = useViewAs();
  const { sees } = usePermissions();
  const canOrganize = sees(ACTIONS.SECURE_EXCHANGE_ORGANIZE);
  const fileActions = useFileActions();
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim());
  const [sort, setSort] = useState<FileSort | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [uploadOpen, setUploadOpen] = useState(false);

  const {
    data: collection,
    isLoading,
    isError,
  } = useGetSecureCollectionQuery(collectionId, { skip: !collectionId });
  const missing = !collectionId || isError;
  const {
    data: files,
    isLoading: loadingFiles,
    isFetching,
  } = useGetSecureFilesQuery(
    {
      tab: "live",
      collection: collectionId,
      search: search || undefined,
      sort_by: sort?.column ?? null,
      sort_order: sort?.direction ?? null,
      page,
      page_size: limit,
    },
    { skip: missing },
  );

  if (
    collection &&
    (collection.client_id !== clientId || collection.project_id !== projectId)
  )
    return (
      <Navigate
        replace
        to={paths.collection(
          collection.client_id,
          collection.project_id,
          collection.id,
        )}
      />
    );

  const total = files?.count ?? 0;
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <SecureExchangeShell
      title={
        collection ? `${collection.name} - Secure Exchange` : "Secure Exchange"
      }
      breadcrumbs={
        <SecureExchangeBreadcrumbs
          items={[
            paths.home,
            {
              label: collection?.client_name ?? "",
              to: clientId ? paths.client(clientId) : undefined,
            },
            {
              label: collection?.project_name ?? "",
              to:
                clientId && projectId
                  ? paths.project(clientId, projectId)
                  : undefined,
            },
            { label: collection?.name ?? "" },
          ]}
        />
      }
      header={
        <SecureExchangeHeader
          icon={<CollectionIcon />}
          title={missing ? "Collection not found" : collection?.name}
          subtitle={collection?.project_name}
          loading={isLoading}
        />
      }
    >
      {missing ? (
        <Typography sx={{ p: 3 }} color="text.secondary">
          This collection is not in your Secure Exchange.
        </Typography>
      ) : (
        <>
          <ListToolbar
            search={searchInput}
            onSearch={(value) => {
              setSearchInput(value);
              setPage(1);
            }}
            placeholder="Search this collection"
            actions={
              collection?.project_status === "open" ? (
                <ReadOnlyTooltip active={readOnly}>
                  <Button
                    variant="contained"
                    disabled={readOnly}
                    onClick={() => setUploadOpen(true)}
                  >
                    Upload New File
                  </Button>
                </ReadOnlyTooltip>
              ) : null
            }
          />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              opacity: isFetching && !loadingFiles ? 0.6 : 1,
            }}
          >
            <SecureFileTable
              files={files?.results ?? []}
              loading={loadingFiles}
              sort={sort}
              onSort={(column) => {
                setSort((current) => nextFileSort(current, column));
                setPage(1);
              }}
              empty={
                search
                  ? "No files in this collection match."
                  : "No files in this collection yet."
              }
              readOnly={readOnly}
              canOrganize={canOrganize}
              onAction={fileActions.run}
            />
          </Box>
          {total > 0 ? (
            <PaginationControls
              currentLimit={limit}
              onLimitChange={(next) => {
                setLimit(next);
                setPage(1);
              }}
              totalItems={total}
              currentPage={page}
              totalPages={pages}
              onPageChange={setPage}
              size="small"
              hasNextPage={page < pages}
            />
          ) : null}
        </>
      )}
      {fileActions.dialogs}
      {collection ? (
        <SecureUploadDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          clientId={collection.client_id}
          clientName={collection.client_name}
          place={{
            projectId: collection.project_id,
            projectName: collection.project_name,
            collectionId: collection.id,
            collectionName: collection.name,
          }}
        />
      ) : null}
    </SecureExchangeShell>
  );
}
